import React from 'react';
import PropTypes from 'prop-types';
import { surfaces, borders, textColors, typography } from '../../tokens';

/**
 * Input Component
 * 
 * Standardized text input component for startup UI forms.
 * Supports various input types, sizes, states, and icon decorations.
 * 
 * @example
 * // Basic input
 * <Input
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   placeholder="Enter email"
 * />
 * 
 * @example
 * // Input with error state
 * <Input
 *   value={username}
 *   onChange={(e) => setUsername(e.target.value)}
 *   error="Username is required"
 * />
 * 
 * @example
 * // Input with icon
 * <Input
 *   type="email"
 *   value={email}
 *   onChange={(e) => setEmail(e.target.value)}
 *   leftIcon={<MailIcon />}
 * />
 */
const Input = React.forwardRef(({
  // Value and change handlers
  value = '',
  onChange,
  onBlur,
  onFocus,
  
  // Input attributes
  type = 'text',
  name,
  id,
  placeholder,
  disabled = false,
  readOnly = false,
  required = false,
  autoComplete,
  autoFocus = false,
  maxLength,
  
  // Sizing
  size = 'md', // 'sm' | 'md' | 'lg'
  fullWidth = true,
  
  // States
  error = '',
  success = '',
  helperText = '',
  
  // Icons
  leftIcon,
  rightIcon,
  rightAdornment,
  
  // Styling
  className = '',
  
  // Accessibility
  'aria-label': ariaLabel,
  'aria-describedby': ariaDescribedBy,
  'aria-invalid': ariaInvalid,
  
  // Additional props
  ...rest
}, ref) => {
  // Determine state
  const hasError = Boolean(error);
  const hasSuccess = Boolean(success) && !hasError;
  const hasHelperText = Boolean(helperText) && !hasError && !hasSuccess;
  
  // Auto-generate IDs for aria-describedby
  const errorId = id ? `${id}-error` : undefined;
  const successId = id ? `${id}-success` : undefined;
  const helperId = id ? `${id}-helper` : undefined;
  
  // Build aria-describedby
  let describedBy = ariaDescribedBy || '';
  if (hasError && errorId) describedBy = describedBy ? `${describedBy} ${errorId}` : errorId;
  if (hasSuccess && successId) describedBy = describedBy ? `${describedBy} ${successId}` : successId;
  if (hasHelperText && helperId) describedBy = describedBy ? `${describedBy} ${helperId}` : helperId;
  
  // Size classes
  const sizeClasses = {
    sm: 'px-3 py-1.5 text-sm',
    md: 'px-3 py-2 text-base',
    lg: 'px-4 py-2.5 text-lg',
  };
  
  // State classes
  const baseClasses = `border rounded-md shadow-sm transition-colors duration-200 focus:outline-none bg-white/50 dark:bg-dark-800/50 backdrop-blur-sm`;
  
  let stateClasses = '';
  if (disabled) {
    stateClasses = `${surfaces.tertiary} ${textColors.tertiary} border-gray-300 dark:border-dark-700 cursor-not-allowed`;
  } else if (readOnly) {
    stateClasses = `${surfaces.tertiary} ${textColors.tertiary} border-gray-300 dark:border-dark-700 cursor-not-allowed`;
  } else if (hasError) {
    stateClasses = `border-red-300 dark:border-red-700 ${textColors.primary} placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-red-500 dark:focus:ring-red-400 focus:border-red-500 dark:focus:border-red-400`;
  } else if (hasSuccess) {
    stateClasses = `border-green-300 dark:border-green-700 ${textColors.primary} placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-green-500 dark:focus:ring-green-400 focus:border-green-500 dark:focus:border-green-400`;
  } else {
    stateClasses = `${borders.default} ${textColors.primary} placeholder-gray-400 dark:placeholder-dark-500 focus:ring-2 focus:ring-blue-600 dark:focus:ring-blue-500 focus:border-blue-600 dark:focus:border-blue-500`;
  }
  
  // Width class
  const widthClass = fullWidth ? 'w-full' : '';
  
  const hasRightDecoration = Boolean(rightIcon) || Boolean(rightAdornment);

  // Icon padding adjustments
  const iconPaddingClasses = {
    left: leftIcon ? 'pl-10' : '',
    // Use a slightly larger padding for clickable adornments (IconButton is typically 40px wide)
    right: hasRightDecoration ? 'pr-12' : '',
  };
  
  // Combine all classes
  const inputClasses = [
    baseClasses,
    sizeClasses[size],
    stateClasses,
    widthClass,
    iconPaddingClasses.left,
    iconPaddingClasses.right,
    className,
  ].filter(Boolean).join(' ');
  
  return (
    <div className={fullWidth ? 'w-full' : 'inline-block'}>
      {/* Input with optional icons */}
      <div className="relative">
        {/* Left Icon */}
        {leftIcon && (
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <div className={hasError ? 'text-red-500 dark:text-red-400' : hasSuccess ? 'text-green-500 dark:text-green-400' : `${textColors.tertiary}`}>
              {leftIcon}
            </div>
          </div>
        )}
        
        {/* Input Element */}
        <input
          ref={ref}
          type={type}
          value={value}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          name={name}
          id={id}
          placeholder={placeholder}
          disabled={disabled}
          readOnly={readOnly}
          required={required}
          autoComplete={autoComplete}
          autoFocus={autoFocus}
          maxLength={maxLength}
          className={inputClasses}
          aria-label={ariaLabel}
          aria-describedby={describedBy || undefined}
          aria-invalid={ariaInvalid !== undefined ? ariaInvalid : hasError}
          {...rest}
        />
        
        {/* Right Icon */}
        {rightAdornment ? (
          <div className="absolute inset-y-0 right-0 pr-1 flex items-center">
            {rightAdornment}
          </div>
        ) : rightIcon ? (
          <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
            <div className={hasError ? 'text-red-500 dark:text-red-400' : hasSuccess ? 'text-green-500 dark:text-green-400' : `${textColors.tertiary}`}>
              {rightIcon}
            </div>
          </div>
        ) : null}
      </div>
      
      {/* Error Message */}
      {hasError && (
        <p id={errorId} className={`mt-1 ${typography.caption} text-red-600 dark:text-red-400`}>
          {error}
        </p>
      )}
      
      {/* Success Message */}
      {hasSuccess && (
        <p id={successId} className={`mt-1 ${typography.caption} text-green-600 dark:text-green-400`}>
          {success}
        </p>
      )}
      
      {/* Helper Text */}
      {hasHelperText && (
        <p id={helperId} className={`mt-1 ${typography.caption} ${textColors.tertiary}`}>
          {helperText}
        </p>
      )}
    </div>
  );
});

Input.displayName = 'Input';

Input.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  
  type: PropTypes.oneOf(['text', 'email', 'password', 'number', 'url', 'tel', 'search']),
  name: PropTypes.string,
  id: PropTypes.string,
  placeholder: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  required: PropTypes.bool,
  autoComplete: PropTypes.string,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  
  size: PropTypes.oneOf(['sm', 'md', 'lg']),
  fullWidth: PropTypes.bool,
  
  error: PropTypes.string,
  success: PropTypes.string,
  helperText: PropTypes.string,
  
  leftIcon: PropTypes.node,
  rightIcon: PropTypes.node,
  rightAdornment: PropTypes.node,
  
  className: PropTypes.string,
  
  'aria-label': PropTypes.string,
  'aria-describedby': PropTypes.string,
  'aria-invalid': PropTypes.bool,
};

export default Input;
